import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { ChevronLeft } from "lucide-react";
import { resetpassword } from "services/authService";

export default function ResetPasswordPageCrew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const resetToken = searchParams.get("token");

  const [form, setForm] = useState({
    newPassword: "",
    confirmPassword: ""
  });

  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const videos = [
    "/images/login1.mp4",
    "/images/login3.mp4",
    "/images/login2.mp4"
  ];
  const [currentVideo, setCurrentVideo] = useState(0);

  useEffect(() => {
    const interval = setInterval(
      () => setCurrentVideo((prev) => (prev + 1) % videos.length),
      2000
    );
    return () => clearInterval(interval);
  }, []);

  const handleReset = async () => {
    setErrorMsg("");

    if (!form.newPassword || !form.confirmPassword) {
      setErrorMsg("Please fill all fields");
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setErrorMsg("Passwords do not match");
      return;
    }

    try {
      const res = await resetpassword({
        resetToken,
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword,
      });

      if (res?.message === "Password has been reset successfully") {
        setSuccess(true);

        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 1000);

      } else {
        setErrorMsg(res?.message || "Reset failed");
      }
    } catch (err) {
      setErrorMsg("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="w-full h-screen flex overflow-hidden">

      {/* SUCCESS OVERLAY */}
      {success && (
        <div
          className="absolute inset-0 flex items-center justify-center z-50 px-4"
          style={{
            background: "linear-gradient(to bottom, #fdfcfb, #e7d7bd)",
          }}
        >
          <div
            className="bg-white w-full max-w-md rounded-3xl shadow-2xl p-16 pt-28 text-center relative flex flex-col items-center"
            style={{ minHeight: "600px" }}
          >
            {/* LONG Black Pill */}
            <div
              className="bg-black rounded-full flex items-center justify-center shadow-md mb-20"
              style={{ width: "330px", height: "50px" }}
            >
              <img src="/images/be.png" alt="Logo" className="h-7 object-contain" />
            </div>

            <h2 className="text-3xl font-extrabold text-gray-900 mb-5 leading-snug">
              Password Reset<br />Successful
            </h2>

            <p className="text-gray-500 mb-12 text-sm max-w-xs">
              You can now sign in with your new password.
            </p>

            <Button
              className="w-full bg-black text-white py-3 rounded-lg mb-14"
              onClick={() => navigate("/loginCrew")}
            >
              Sign in
            </Button>

            <p className="text-sm text-gray-500">
              If you didn’t request this change{" "}
              <span className="font-semibold text-gray-800 cursor-pointer">
                Contact Support.
              </span>
            </p>
          </div>
        </div>
      )}

      {/* LEFT FORM SECTION */}
      <div className="w-full lg:w-1/2 bg-[#fdf9f2] flex flex-col h-full px-4 lg:px-14 py-14 lg:py-10">

        {/* TOP: BACK + LOGO */}
        <div className="w-full flex justify-center">
          <div className="max-w-md w-full flex items-center justify-between mb-8">
            <Link to="/loginCrew" className="flex items-center text-gray-600 text-sm">
              <ChevronLeft size={18} className="mr-1" /> Back
            </Link>

            <img src="/images/Container.png" alt="Logo" className="w-24 object-contain" />
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="flex flex-col flex-grow justify-center max-w-md w-full mx-auto">
          <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-2 text-center">
            Reset Your Password
          </h2>

          <p className="text-gray-500 mb-8 text-center">
            Create a new password for your account.
          </p>

          <div className="space-y-5">

            {/* ERROR MESSAGE */}
            {errorMsg && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 text-sm rounded">
                {errorMsg}
              </div>
            )}

            {/* New Password */}
            <div>
              <label className="text-sm font-medium mb-1 block">New Password</label>
              <Input
                type="password"
                placeholder="Enter new password"
                value={form.newPassword}
                onChange={(e) =>
                  setForm({ ...form, newPassword: e.target.value })
                }
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label className="text-sm font-medium mb-1 block">Confirm Password</label>
              <Input
                type="password"
                placeholder="Re-enter new password"
                value={form.confirmPassword}
                onChange={(e) => {
                  setForm({ ...form, confirmPassword: e.target.value });
                  setErrorMsg("");
                }}
              />
            </div>

            <Button
              className="w-full bg-black text-white py-3"
              onClick={handleReset}
              disabled={loading}
            >
              {loading ? "Please wait..." : "Reset Password"}
            </Button>
          </div>


          {/* SIGN UP TEXT WITH LINES */}
          <div className="relative flex items-center justify-center mt-12 mb-8">
            <div className="flex-grow h-px bg-gray-300"></div>

            <p className="mx-4 text-sm text-gray-600">
              Don’t have an account?{" "}
              <Link to="/signup" className="font-medium text-black hover:underline">
                Sign Up
              </Link>
            </p>

            <div className="flex-grow h-px bg-gray-300"></div>
          </div>
        </div>
      </div>

      {/* RIGHT VIDEO SECTION (unchanged) */}
      <div className="hidden lg:flex w-1/2 relative">
        {videos.map((src, index) => (
          <video
            key={index}
            src={src}
            muted
            autoPlay
            loop
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${currentVideo === index ? "opacity-100" : "opacity-0"
              }`}
          />
        ))}

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>

        {/* Captions + Dots */}
        <div className="absolute bottom-10 left-0 w-full text-center text-white px-10 z-20">
          {currentVideo === 0 && (
            <>
              <h3 className="text-2xl font-bold">Editor</h3>
              <p className="text-sm opacity-90 mt-2">
                Bring your creative vision to life with intuitive editing tools.
              </p>
            </>
          )}

          {currentVideo === 1 && (
            <>
              <h3 className="text-2xl font-bold">Photography</h3>
              <p className="text-sm opacity-90 mt-2">
                Join a lively network of skilled photographers.
              </p>
            </>
          )}

          {currentVideo === 2 && (
            <>
              <h3 className="text-2xl font-bold">Videography</h3>
              <p className="text-sm opacity-90 mt-2">
                Connect with a vibrant community of videographers.
              </p>
            </>
          )}

          <div className="flex justify-center gap-2 mt-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${currentVideo === i ? "bg-white" : "bg-white/60"
                  }`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
